'use client'

import { useState } from 'react'
import { useRouter } from 'next/navigation'
import { Button } from '@/components/ui/button'
import { Loader2 } from 'lucide-react'
import Link from 'next/link'

interface ReviewDisclaimerProps {
  className?: string
  variant?: 'mobile' | 'desktop'
}

export default function ReviewDisclaimer({ className, variant = 'mobile' }: ReviewDisclaimerProps) {
  const [isLoading, setIsLoading] = useState(false)
  const router = useRouter()

  const handleReportClick = async (e: React.MouseEvent) => {
    e.preventDefault()
    setIsLoading(true)
    
    try {
      router.push('/report')
    } catch (error) {
      console.error('Navigation error:', error)
      setIsLoading(false)
    }
    // Note: Don't set loading to false here as navigation will unmount the component
  }

  const baseClasses = "bg-gradient-to-r from-amber-50 to-orange-50 border-l-4 border-amber-400 rounded-lg p-4 shadow-sm"
  
  if (variant === 'desktop') {
    return (
      <div className={`${baseClasses} ${className || ''}`}>
        <div className="flex flex-col gap-4">
          <div className="flex items-start gap-3">
            <div className="bg-amber-100 p-2 rounded-full shrink-0">
              <svg className="h-4 w-4 text-amber-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            </div>
            <div className="flex-1">
              <p className="text-sm text-amber-800 leading-relaxed">
                Reviews on RentWise are submitted by verified tenants. While we make efforts to ensure accuracy, we cannot guarantee that all information is complete or current. Please verify critical details before making rental decisions.
              </p>
            </div>
          </div>
          <div>
            <Button 
              onClick={handleReportClick}
              disabled={isLoading}
              variant="outline" 
              size="sm"
              className="w-full bg-red-50 border-red-300 text-red-700 hover:bg-red-100 hover:border-red-400 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  Loading...
                </>
              ) : (
                'Report Property'
              )}
            </Button>
          </div>
        </div>
      </div>
    )
  }

  // Mobile variant
  return (
    <div className={`${baseClasses} ${className || ''}`}>
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-start gap-3">
          <div className="bg-amber-100 p-2 rounded-full shrink-0 mt-0.5 sm:mt-0">
            <svg className="h-4 w-4 text-amber-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>
          <div className="flex-1">
            <p className="text-sm text-amber-800 leading-relaxed">
              Reviews on RentWise are submitted by verified tenants. While we make efforts to ensure accuracy, we cannot guarantee that all information is complete or current. Please verify critical details before making rental decisions.
            </p>
          </div>
        </div>
        <div className="sm:shrink-0">
          <Button 
            onClick={handleReportClick}
            disabled={isLoading}
            variant="outline" 
            size="sm"
            className="w-full sm:w-auto bg-red-50 border-red-300 text-red-700 hover:bg-red-100 hover:border-red-400 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isLoading ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
                Loading...
              </>
            ) : (
              'Report Property'
            )}
          </Button>
        </div>
      </div>
    </div>
  )
} 